import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProfileModal from './ProfileModal';
import NotificationBell from './NotificationBell';
import DeleteBookmarkDialog from './DeleteBookmarkDialog';
import PendingInvites from './PendingInvites';
import CurrentlyViewing from './CurrentlyViewing';
import axios from 'axios';
import {
  Home,
  Search as SearchIcon,
  ChevronDown as ChevronDownIcon,
  AddLarge,
  Edit,
  TrashCan,
  Bookmark as BookmarkIcon,
  Checkmark,
  Close,
  Settings,
  Star,
  StarFilled,
  CloseFilled,
} from '@carbon/icons-react';
import { Menu, X, PanelLeftOpen, PanelLeftClose, Plus, Bell } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import NikeNavIcon from './icons/NikeNavIcons';
import ClaudeToolIcon from './icons/ClaudeToolIcons';
import EVERYTHING_CARDS from '../pages/everything/cardsList';
import logoBlack from '../assets/images/LOGO-MAIN-BLACK.png';
import logoWhite from '../assets/images/LOGO-MAIN-WHITE.png';
import { NAV_GROUP_BY_ID, buildNavTree, groupIdOf, visibleNavIds } from './sidebar/navConfig';
import { groupedToolsFor } from '../pages/tools/toolsList';
import { pushToolsMru, userToolKey } from '../pages/tools/toolsMru';
import useAcpAccess from '../pages/acp/useAcpAccess';
import ToolMonogram from '../toolbuilder/ToolMonogram';
import SidebarToolsMenu from '../toolbuilder/SidebarToolsMenu';
import { useSidebarTools } from '../toolbuilder/sidebarTools';
import { trackFeatureEvent } from '../utils/featureEvents';

const softSpringEasing = 'cubic-bezier(0.25, 1.1, 0.4, 1)';

// Shared "tooltip group" timer so sweeping the icon rail doesn't re-fire the
// open-delay on every item. First tip after entering the rail waits ~130ms;
// while the cursor stays in the rail, subsequent tips appear instantly.
let lastNavTipAt = 0;

/* ------------------------------ Avatar -------------------------------- */
function AvatarCircle({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative rounded-full shrink-0 size-8 bg-gray-200 dark:bg-neutral-800 hover:bg-gray-300 dark:hover:bg-neutral-700 transition-colors cursor-pointer"
    >
      <div className="flex items-center justify-center size-8 text-gray-700 dark:text-neutral-300">
        <NikeNavIcon name="user" size={16} />
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-full border border-gray-300 dark:border-neutral-700 pointer-events-none"
      />
    </button>
  );
}

/* ---------------------------- Left Icon Nav -------------------------- */
// Tooltip styling mirrors `StatCard` on /goals (see claraf/src/pages/Goals.jsx
// line 91-99) — same warm dark surface, same look.
//
// Rendered via portal into document.body because the sidebar shell uses
// `overflow-x-auto` (so multi-pane layouts can scroll horizontally on mobile);
// an absolute tooltip inside the rail would contribute to that shell's
// scrollWidth and produce phantom scrollbars + clipping. A portaled `fixed`
// tooltip is positioned from the button's viewport rect and is unaffected by
// any ancestor's overflow. The folder flyouts below use the same trick.
//
// What the rail shows, and to whom, is decided in ./sidebar/navConfig.js
// (pages, folders, order, role defaults) — this file only renders the tree.

// Rail geometry. Collapsed is the icon rail everyone knows; expanded is
// deliberately only "a little" wider — room for the longest label
// ("Everything & Anything" at 13px) and nothing more. The live width is
// published as the --app-rail-w CSS variable so the two things that position
// themselves off the rail (PageMorph's curtain, TitleNavigation on the canvas)
// follow it instead of assuming 64px.
const RAIL_W = 64;
const RAIL_W_EXPANDED = 224;
const RAIL_EXPANDED_KEY = 'clara.sidebar.expanded';
const RAIL_OPEN_GROUPS_KEY = 'clara.sidebar.openGroups';

const readStoredExpanded = () => {
  try {
    return localStorage.getItem(RAIL_EXPANDED_KEY) === '1';
  } catch {
    return false;
  }
};

const readStoredOpenGroups = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(RAIL_OPEN_GROUPS_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw.filter((id) => NAV_GROUP_BY_ID[id]) : []);
  } catch {
    return new Set();
  }
};

// Wordmark geometry. LOGO-MAIN-*.png is 2000×500 and the "C" mark occupies
// x≈95–510 of it. Rendered 22px tall the whole word is 88px wide and the mark
// alone fits in the first 24px — so ONE asset serves both rail states: a clip
// box that grows from 24px (mark) to 88px (full "Clara") reveals the rest of
// the word while the mark itself never moves a pixel.
const LOGO_H = 22;
const LOGO_W_MARK = 24;
const LOGO_W_FULL = 88;

/**
 * Portaled label tooltip for a rail control. `enabled` false suppresses it —
 * the expanded rail shows labels inline, so a tip there would be noise.
 */
function useRailTip(label, enabled = true) {
  const ref = React.useRef(null);
  const timerRef = React.useRef(null);
  const [tip, setTip] = React.useState(null); // { top, left } or null

  const show = React.useCallback(() => {
    const el = ref.current;
    if (!el || !label || !enabled) return;
    const r = el.getBoundingClientRect();
    const pos = { top: r.top + r.height / 2, left: r.right + 12 };
    // Skip the open-delay if another rail tip was active very recently.
    const delay = Date.now() - lastNavTipAt < 500 ? 0 : 130;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setTip(pos); lastNavTipAt = Date.now(); }, delay);
  }, [label, enabled]);

  const hide = React.useCallback(() => {
    clearTimeout(timerRef.current);
    setTip(null);
    lastNavTipAt = Date.now();
  }, []);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);
  // The rail expanded under the cursor: drop a tip that is already showing.
  React.useEffect(() => {
    if (!enabled) {
      clearTimeout(timerRef.current);
      setTip(null);
    }
  }, [enabled]);

  const node = createPortal(
    <AnimatePresence>
      {tip && (
        <motion.div
          role="tooltip"
          key="nav-tip"
          initial={{ opacity: 0, x: -4, y: '-50%', scale: 0.96 }}
          animate={{ opacity: 1, x: 0, y: '-50%', scale: 1 }}
          exit={{ opacity: 0, x: -2, y: '-50%' }}
          transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
          className="fixed pointer-events-none px-2.5 py-1.5 rounded-md bg-[#111111] dark:bg-white text-white dark:text-[#111111] text-[11px] font-bold uppercase tracking-[0.12em] leading-none shadow-lg whitespace-nowrap z-[100]"
          style={{ top: tip.top, left: tip.left, transformOrigin: 'left center' }}
        >
          {label}
          <span aria-hidden="true" className="absolute right-full top-1/2 -translate-y-1/2 -mr-1 w-2 h-2 rotate-45 bg-[#111111] dark:bg-white" />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );

  return { ref, show, hide, node };
}

/**
 * One rail control. Collapsed: a 40px square with a tooltip. Expanded: a full
 * row — the same 40px icon box on the left (so icons never shift when the rail
 * opens), the label beside it, and an optional trailing slot (folder chevron).
 * `compact` is the smaller row used for pages nested under an open folder.
 * Remaining props (aria-*) land on the button.
 */
function IconNavButton({
  children,
  isActive = false,
  onClick,
  label,
  unreadDot = false,
  expanded = false,
  compact = false,
  trailing = null,
  tip = true,
  buttonRef,
  onHoverStart,
  onHoverEnd,
  ...rest
}) {
  const reduce = useReducedMotion();
  const { ref: tipRef, show, hide, node } = useRailTip(label, tip && !expanded);
  const setRefs = React.useCallback((el) => {
    tipRef.current = el;
    if (buttonRef) buttonRef.current = el;
  }, [tipRef, buttonRef]);

  const box = compact ? 'size-8 min-w-8' : 'size-10 min-w-10';
  const radius = compact ? 'rounded-lg' : 'rounded-xl';

  return (
    <>
      <motion.button
        ref={setRefs}
        type="button"
        aria-label={label}
        onClick={onClick}
        onMouseEnter={() => { show(); onHoverStart?.(); }}
        onMouseLeave={() => { hide(); onHoverEnd?.(); }}
        onFocus={show}
        onBlur={hide}
        initial={false}
        animate={isActive ? 'active' : 'rest'}
        whileHover={reduce ? undefined : 'hover'}
        whileTap={reduce ? undefined : 'tap'}
        className={`relative flex items-center outline-none transition-colors duration-200 ${radius}
          ${expanded ? `w-full ${compact ? 'h-8' : 'h-10'}` : `${box} justify-center`}
          ${isActive
            ? 'text-[#111111] dark:text-white'
            : 'text-[#9e9ea0] dark:text-white/45 hover:text-[#111111] dark:hover:text-white'}`}
        {...rest}
      >
        {/* Shared active pill — slides between items via layoutId (FLIP). */}
        {isActive && (
          <motion.span
            layoutId="nav-active"
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            className={`absolute inset-0 ${radius} bg-[#f5f5f5] dark:bg-white/[0.08]`}
          />
        )}
        <span className={`relative z-10 flex items-center justify-center shrink-0 ${box}`}>{children}</span>
        <span
          aria-hidden={!expanded}
          className={`relative z-10 text-left text-[13px] font-medium tracking-tight whitespace-nowrap overflow-hidden transition-[opacity,transform] duration-200 ${
            expanded
              ? 'flex-1 min-w-0 truncate opacity-100 translate-x-0 pr-2'
              : 'w-0 flex-none opacity-0 -translate-x-1 pointer-events-none'
          }`}
        >
          {label}
        </span>
        {expanded && trailing && (
          <span className="relative z-10 flex items-center shrink-0 pr-2.5">{trailing}</span>
        )}
        {/* Unread indicator dot — used by Home for new What's New posts. */}
        {unreadDot && (
          <span
            aria-hidden="true"
            className={`absolute top-1 z-20 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-black ${expanded ? 'left-7' : 'right-1'}`}
          />
        )}
      </motion.button>
      {node}
    </>
  );
}

/**
 * The platform logo at the top of the rail — and the rail's expand/collapse
 * control. Collapsed it is the "C" mark with an "Expand" tooltip; expanded the
 * clip box widens to reveal the full wordmark and a small chevron appears on
 * hover at the row's end, pointing back the way the rail will fold.
 */
function RailLogo({ expanded, onToggle }) {
  const { ref, show, hide, node } = useRailTip('Expand', !expanded);
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={onToggle}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
        aria-expanded={expanded}
        className={`group relative flex items-center h-10 rounded-xl outline-none transition-colors duration-200 hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] focus-visible:bg-[#f5f5f5] dark:focus-visible:bg-white/[0.06] ${
          expanded ? 'w-full' : 'size-10 min-w-10'
        }`}
      >
        <span
          aria-hidden="true"
          className="absolute top-1/2 -translate-y-1/2 left-[7px] block overflow-hidden transition-[width] duration-300 motion-reduce:transition-none"
          style={{ height: LOGO_H, width: expanded ? LOGO_W_FULL : LOGO_W_MARK, transitionTimingFunction: softSpringEasing }}
        >
          <img src={logoBlack} alt="" draggable={false} className="block h-full w-auto max-w-none dark:hidden" />
          <img src={logoWhite} alt="" draggable={false} className="hidden h-full w-auto max-w-none dark:block" />
        </span>
        <span
          aria-hidden="true"
          className={`ml-auto mr-3 text-[#9e9ea0] dark:text-white/40 transition-opacity duration-200 ${
            expanded ? 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100' : 'hidden'
          }`}
        >
          <ChevronDownIcon size={14} className="rotate-90" />
        </span>
      </button>
      {node}
    </>
  );
}

/** A folder in the EXPANDED rail: a header row with its pages nested beneath. */
function GroupAccordion({ group, activeSection, isOpen, onToggle, onNavigate }) {
  const containsActive = group.items.some((item) => item.id === activeSection);
  const panelId = `nav-group-${group.id}`;
  return (
    <div className="w-full">
      <IconNavButton
        expanded
        tip={false}
        label={group.label}
        // The pill sits on the active page while the folder is open and moves
        // up onto the header the moment it closes — the active state is never
        // out of sight.
        isActive={containsActive && !isOpen}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        trailing={(
          <ChevronDownIcon
            size={14}
            className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
            style={{ transitionTimingFunction: softSpringEasing }}
          />
        )}
      >
        <NikeNavIcon name={group.iconName} active={containsActive} />
      </IconNavButton>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={panelId}
            key="pages"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            {/* Guide line drops from the centre of the folder icon (20px in). */}
            <div className="relative ml-[19px] pl-1.5 my-1 flex flex-col gap-0.5 border-l border-[#e5e5e5] dark:border-white/10">
              {group.items.map((item) => (
                <IconNavButton
                  key={item.id}
                  expanded
                  compact
                  tip={false}
                  label={item.label}
                  isActive={activeSection === item.id}
                  onClick={() => onNavigate(item)}
                >
                  <NikeNavIcon name={item.iconName} size={16} active={activeSection === item.id} />
                </IconNavButton>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * A folder in the COLLAPSED rail: one icon. Hovering opens a small menu of
 * its pages beside the rail (same delay rules as the tooltips); clicking pins
 * it open until Esc, a click elsewhere, or a navigation.
 */
function GroupFlyout({ group, activeSection, onNavigate }) {
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const openTimer = React.useRef(null);
  const closeTimer = React.useRef(null);
  const pinnedRef = React.useRef(false);
  const [pos, setPos] = React.useState(null); // { top, left, caret } or null
  const containsActive = group.items.some((item) => item.id === activeSection);

  const clearTimers = React.useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  const openNow = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Keep the whole menu on screen: padding + heading + one 36px row per page.
    const estHeight = 12 + 30 + group.items.length * 36;
    const top = Math.max(8, Math.min(r.top, window.innerHeight - estHeight - 8));
    setPos({ top, left: r.right + 12, caret: r.top + r.height / 2 - top });
  }, [group.items.length]);

  const close = React.useCallback(() => {
    pinnedRef.current = false;
    clearTimers();
    setPos(null);
    lastNavTipAt = Date.now();
  }, [clearTimers]);

  const hoverStart = () => {
    clearTimers();
    openTimer.current = setTimeout(openNow, Date.now() - lastNavTipAt < 500 ? 0 : 130);
  };
  const hoverEnd = () => {
    clearTimeout(openTimer.current);
    if (pinnedRef.current) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPos(null), 180);
  };
  const toggle = () => {
    if (pos && pinnedRef.current) {
      close();
      return;
    }
    pinnedRef.current = true;
    clearTimers();
    openNow();
  };

  // Esc or a click anywhere else closes the menu.
  React.useEffect(() => {
    if (!pos) return undefined;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pos, close]);
  React.useEffect(() => clearTimers, [clearTimers]);

  return (
    <>
      <IconNavButton
        buttonRef={btnRef}
        tip={false}
        label={group.label}
        isActive={containsActive}
        onClick={toggle}
        onHoverStart={hoverStart}
        onHoverEnd={hoverEnd}
        aria-haspopup="menu"
        aria-expanded={!!pos}
      >
        <NikeNavIcon name={group.iconName} active={containsActive} />
      </IconNavButton>
      {createPortal(
        <AnimatePresence>
          {pos && (
            <motion.div
              ref={panelRef}
              key="flyout"
              role="menu"
              aria-label={group.label}
              initial={{ opacity: 0, x: -6, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -4, scale: 0.98 }}
              transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
              onMouseEnter={() => clearTimeout(closeTimer.current)}
              onMouseLeave={hoverEnd}
              className="fixed z-[100] min-w-[188px] p-1.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#141414] shadow-xl"
              style={{ top: pos.top, left: pos.left, transformOrigin: 'left top' }}
            >
              <span
                aria-hidden="true"
                className="absolute -left-1 w-2 h-2 -translate-y-1/2 rotate-45 bg-white dark:bg-[#141414] border-l border-b border-gray-200 dark:border-white/10"
                style={{ top: pos.caret }}
              />
              <div className="px-2.5 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#9e9ea0] dark:text-white/40">
                {group.label}
              </div>
              {group.items.map((item) => {
                const active = item.id === activeSection;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    onClick={() => { close(); onNavigate(item); }}
                    className={`w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-[13px] font-medium tracking-tight text-left transition-colors ${
                      active
                        ? 'bg-[#f5f5f5] dark:bg-white/[0.08] text-[#111111] dark:text-white'
                        : 'text-[#707072] dark:text-white/60 hover:bg-[#f5f5f5] dark:hover:bg-white/[0.05] hover:text-[#111111] dark:hover:text-white'
                    }`}
                  >
                    <NikeNavIcon name={item.iconName} size={16} active={active} />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

function IconNavigation({ activeSection, onSectionChange, onOpenProfile, onMobileClose }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  // Everything & Anything hub: visible to admins/developers always; for everyone
  // else only when they've been granted at least ONE card (admin-managed
  // allow-lists — see /api/everything/my-access).
  const [hasEverythingAccess, setHasEverythingAccess] = React.useState(false);

  // Unread "What's New" posts → red dot on Home icon. Fetched on mount + on
  // each visit to /home (which calls mark-seen via WhatsNew component); we
  // refetch ~1.5s after the visit to pick up the cleared count.
  const [whatsNewUnread, setWhatsNewUnread] = React.useState(0);
  const fetchWhatsNewUnread = React.useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/whats-new/unread-count`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setWhatsNewUnread(data.count || 0);
    } catch (err) {
      // 401 during boot, or BE temporarily unhappy — silently degrade.
      setWhatsNewUnread(0);
    }
  }, []);
  React.useEffect(() => { fetchWhatsNewUnread(); }, [fetchWhatsNewUnread]);
  React.useEffect(() => {
    if (activeSection !== 'home') return;
    const t = setTimeout(fetchWhatsNewUnread, 1500);
    return () => clearTimeout(t);
  }, [activeSection, fetchWhatsNewUnread]);

  // Scores page access — admins always; everyone else asks the backend
  // (MAESTRO_CUTOVER_OFF gate: pre-cutover the API answers hasAccess:false for
  // non-admins, post-cutover it opens up — no frontend redeploy needed).
  const [hasScoresAccess, setHasScoresAccess] = React.useState(false);
  React.useEffect(() => {
    const checkScoresAccess = async () => {
      if (!user?.email) {
        setHasScoresAccess(false);
        return;
      }
      if (user?.role === 'admin') {
        setHasScoresAccess(true);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/scores/my-access`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setHasScoresAccess(Boolean(response.data?.hasAccess));
      } catch (err) {
        // Pre-cutover 403s / boot 401s land here — silently hide the entry.
        setHasScoresAccess(false);
      }
    };
    checkScoresAccess();
  }, [user?.email, user?.role]);

  // Everything & Anything access — role bypass first, then the per-card allow-list API
  React.useEffect(() => {
    const checkEverythingAccess = async () => {
      if (!user?.email) {
        setHasEverythingAccess(false);
        return;
      }
      if (user?.role === 'admin' || user?.role === 'developer') {
        setHasEverythingAccess(true);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/everything/my-access`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Intersect with the FE card registry — a DB grant on a cardId the
        // frontend doesn't ship (stale/mistyped slug) must not surface a nav
        // item that leads to an empty hub.
        const knownCardIds = new Set(EVERYTHING_CARDS.map((c) => c.id));
        setHasEverythingAccess(
          (response.data?.cards || []).some((id) => knownCardIds.has(id))
        );
      } catch (err) {
        console.error('Error checking Everything & Anything access:', err);
        setHasEverythingAccess(false);
      }
    };
    checkEverythingAccess();
  }, [user?.email, user?.role]);

  // ── Expanded / collapsed ──────────────────────────────────────────────
  // Remembered per browser; collapsed by default so nobody's rail changes
  // shape until they click the logo themselves.
  const [expanded, setExpanded] = React.useState(readStoredExpanded);
  const toggleExpanded = React.useCallback(() => setExpanded((v) => !v), []);
  React.useEffect(() => {
    try { localStorage.setItem(RAIL_EXPANDED_KEY, expanded ? '1' : '0'); } catch { /* private mode */ }
    document.documentElement.style.setProperty('--app-rail-w', `${expanded ? RAIL_W_EXPANDED : RAIL_W}px`);
  }, [expanded]);

  // ── Which folders are open (expanded rail only) ──────────────────────
  // Remembered too, and the folder holding the current page opens itself
  // whenever the current page moves into a folder.
  const [openGroups, setOpenGroups] = React.useState(readStoredOpenGroups);
  const activeGroupId = groupIdOf(activeSection);
  React.useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((prev) => (prev.has(activeGroupId) ? prev : new Set([...prev, activeGroupId])));
  }, [activeGroupId]);
  React.useEffect(() => {
    try { localStorage.setItem(RAIL_OPEN_GROUPS_KEY, JSON.stringify([...openGroups])); } catch { /* private mode */ }
  }, [openGroups]);
  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  // The tree: role defaults + admin overrides + the two API answers, folded
  // by the grouping rule (navConfig.js). Chat / Workspaces / Admin History
  // are intentionally absent — the routes exist, they just have no entry.
  // ACP page — the one ACP access function (clarab/services/acpAccessService):
  // the login email owns a stake.com ACP account with a support-team role;
  // admins/developers always. Same hook as the page (pages/acp/useAcpAccess).
  const { granted: hasAcpAccess } = useAcpAccess();
  const tree = buildNavTree(visibleNavIds(user, { hasScoresAccess, hasEverythingAccess, hasAcpAccess }));

  const handleLogout = () => {
    logout();
    navigate('/login');
    onMobileClose?.();
  };

  // Every page carries its own path (navConfig.js), which is exactly where
  // the hosts' onSectionChange handlers used to send `/${id}` — so the rail
  // navigates itself and closes the mobile drawer (if open) on the way.
  const handleNavigate = (item) => {
    onMobileClose?.();
    navigate(item.path);
  };

  const renderEntry = (entry) => {
    if (entry.type === 'group') {
      return expanded ? (
        <GroupAccordion
          group={entry}
          activeSection={activeSection}
          isOpen={openGroups.has(entry.id)}
          onToggle={() => toggleGroup(entry.id)}
          onNavigate={handleNavigate}
        />
      ) : (
        <GroupFlyout group={entry} activeSection={activeSection} onNavigate={handleNavigate} />
      );
    }
    const isActive = activeSection === entry.id;
    return (
      <IconNavButton
        expanded={expanded}
        isActive={isActive}
        onClick={() => handleNavigate(entry)}
        label={entry.label}
        unreadDot={entry.id === 'home' && whatsNewUnread > 0}
      >
        <NikeNavIcon name={entry.iconName} active={isActive} />
      </IconNavButton>
    );
  };

  const displayName = user?.name || user?.email?.split('@')[0] || '';

  return (
    <aside
      className="bg-white dark:bg-black flex flex-col items-stretch gap-2 px-3 py-4 h-screen border-r border-gray-200 dark:border-neutral-800 shrink-0 transition-[width] duration-300 motion-reduce:transition-none"
      style={{ width: expanded ? RAIL_W_EXPANDED : RAIL_W, transitionTimingFunction: softSpringEasing }}
      data-expanded={expanded ? 'true' : 'false'}
    >
      {/* Mobile-only close button — rendered inside the column so it pushes the
          logo down instead of sitting on top of it. Hidden on lg+. */}
      {onMobileClose && (
        <button
          type="button"
          onClick={onMobileClose}
          className="lg:hidden flex items-center justify-center rounded-lg size-10 min-w-10 hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-neutral-50 transition-colors"
          aria-label="Close menu"
        >
          <X size={16} />
        </button>
      )}

      <RailLogo expanded={expanded} onToggle={toggleExpanded} />

      {/* Navigation — staggered fade-in on mount (NOT per route change). Scrolls
          on short screens; the scrollbar is hidden, tooltips/flyouts are
          portaled so the overflow never clips them. */}
      <motion.nav
        aria-label="Main"
        className="flex flex-col gap-1 w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.03 } } }}
      >
        {tree.map((entry) => (
          <motion.div
            key={entry.type === 'group' ? `group:${entry.id}` : entry.id}
            className="w-full"
            variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] } } }}
          >
            {renderEntry(entry)}
          </motion.div>
        ))}
      </motion.nav>

      {/* Bottom section */}
      <div className="flex flex-col gap-2 w-full">
        {/* Everyone's inbox (2026-11-03): coaching, appeal and review
            notifications. QA Manager keeps its header bell, which also carries
            the live review/appeal queues — one bell per screen. */}
        {activeSection !== 'qa-manager' && (
          <NotificationBell
            renderTrigger={({ open, onToggle, unreadCount, buttonRef }) => (
              <IconNavButton
                expanded={expanded}
                onClick={onToggle}
                buttonRef={buttonRef}
                isActive={open}
                tip={!open}
                label={unreadCount > 0 ? `Notifications (${unreadCount})` : 'Notifications'}
                unreadDot={unreadCount > 0}
                aria-haspopup="dialog"
                aria-expanded={open}
                data-rail-notifications=""
              >
                <Bell size={18} strokeWidth={1.75} />
              </IconNavButton>
            )}
          />
        )}
        <IconNavButton expanded={expanded} onClick={handleLogout} label="Sign out">
          <NikeNavIcon name="logout" />
        </IconNavButton>
        <div className={`flex items-center h-10 ${expanded ? 'w-full' : 'size-10 min-w-10 justify-center'}`}>
          <div className="flex items-center justify-center size-10 min-w-10 shrink-0">
            <AvatarCircle onClick={onOpenProfile} />
          </div>
          <span
            aria-hidden={!expanded}
            className={`text-[13px] font-medium tracking-tight text-[#111111] dark:text-white whitespace-nowrap overflow-hidden transition-opacity duration-200 ${
              expanded ? 'flex-1 min-w-0 truncate opacity-100 pr-2' : 'w-0 opacity-0'
            }`}
          >
            {displayName}
          </span>
        </div>
      </div>
    </aside>
  );
}

/* ------------------------------ Right Sidebar ----------------------------- */
function SectionTitle({ title, onToggleCollapse, isCollapsed, onTitleClick }) {
  if (isCollapsed) {
    return (
      <div
        className="w-full flex justify-center transition-all duration-500"
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex items-center justify-center rounded-lg size-10 min-w-10 transition-all duration-500 hover:bg-[#f5f5f5] dark:hover:bg-white/[0.04] text-[#707072] dark:text-white/55 hover:text-[#111111] dark:hover:text-white"
          style={{ transitionTimingFunction: softSpringEasing }}
          aria-label="Expand sidebar"
        >
          <span className="inline-block rotate-180">
            <ChevronDownIcon size={16} />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="w-full overflow-hidden transition-all duration-500"
      style={{ transitionTimingFunction: softSpringEasing }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center h-10">
          {onTitleClick ? (
            <button
              type="button"
              onClick={onTitleClick}
              className="px-2 py-1 rounded-md hover:bg-[#f5f5f5] dark:hover:bg-white/[0.04] transition-colors"
            >
              <div className="font-semibold text-lg text-[#111111] dark:text-white">{title}</div>
            </button>
          ) : (
            <div className="px-2 py-1">
              <div className="font-semibold text-lg text-[#111111] dark:text-white">{title}</div>
            </div>
          )}
        </div>
        <div className="pr-1">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex items-center justify-center rounded-lg size-10 min-w-10 transition-all duration-500 hover:bg-[#f5f5f5] dark:hover:bg-white/[0.04] text-[#707072] dark:text-white/55 hover:text-[#111111] dark:hover:text-white"
            style={{ transitionTimingFunction: softSpringEasing }}
            aria-label="Collapse sidebar"
          >
            <ChevronDownIcon size={16} className="-rotate-90" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MenuItem({ item, isExpanded, onToggle, onItemClick, isCollapsed, onEdit, onDelete, onSettings, isBookmark, isEditing, editValue, onEditChange, onSaveEdit, onCancelEdit }) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const tooltipTimeoutRef = React.useRef(null);

  const handleClick = (e) => {
    if (isEditing) return; // Don't navigate when editing
    if (item.hasDropdown && onToggle) onToggle();
    else onItemClick?.();
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.();
  };

  const handleSettings = (e) => {
    e.stopPropagation();
    onSettings?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSaveEdit?.();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancelEdit?.();
    }
  };

  const handleMouseEnter = () => {
    if (isCollapsed) {
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 500); // 500ms delay before showing tooltip
    }
  };

  const handleMouseLeave = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setShowTooltip(false);
  };

  React.useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`relative shrink-0 transition-all duration-500 ${
        isCollapsed ? 'w-full flex justify-center' : 'w-full'
      }`}
      style={{ transitionTimingFunction: softSpringEasing }}
    >
      <div
        className={`rounded-lg cursor-pointer transition-all duration-500 flex items-center relative ${
          item.isActive ? 'bg-gray-200 dark:bg-neutral-800' : 'hover:bg-gray-100 dark:hover:bg-neutral-800'
        } ${isCollapsed ? 'w-10 min-w-10 h-10 justify-center p-4' : isBookmark ? 'w-full min-h-[72px] px-4 py-2.5' : 'w-full h-10 px-4 py-2'}`}
        style={{ transitionTimingFunction: softSpringEasing }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Tooltip for collapsed state */}
        {isCollapsed && showTooltip && (
          <div className="absolute left-full ml-2 px-3 py-1.5 bg-gray-900 dark:bg-neutral-700 text-white text-sm rounded-md whitespace-nowrap z-50 pointer-events-none shadow-lg">
            {item.label}
            <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 dark:bg-neutral-700 rotate-45" />
          </div>
        )}
        <div className="flex items-center justify-center shrink-0">{item.icon}</div>

        <div
          className={`flex-1 relative transition-opacity duration-500 overflow-hidden ${
            isCollapsed ? 'opacity-0 w-0' : 'opacity-100 ml-3'
          }`}
          style={{ transitionTimingFunction: softSpringEasing }}
        >
          {isEditing ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => onEditChange?.(e.target.value)}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              className="w-full px-2 py-1 text-sm bg-white dark:bg-neutral-800 border border-blue-500 dark:border-blue-400 rounded text-gray-900 dark:text-neutral-50 focus:outline-none"
            />
          ) : isBookmark && item.preview ? (
            <div className="flex flex-col gap-1">
              {/* Bookmark Custom Name (editable) */}
              <div className="text-sm font-medium text-gray-900 dark:text-neutral-50 truncate">{item.label}</div>
              {/* Element Preview and Details */}
              <div className="flex flex-col gap-0.5">
                <div className="text-xs text-gray-600 dark:text-neutral-400 truncate">{item.preview}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-neutral-500">{item.typeLabel}</span>
                  {item.workspaceIcon && (
                    <>
                      <span className="text-gray-400 dark:text-neutral-600">•</span>
                      <div className="flex items-center gap-1">
                        {item.workspaceIcon}
                        <span className="text-xs text-gray-500 dark:text-neutral-500">{item.workspaceName}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-900 dark:text-neutral-50 truncate">{item.label}</div>
          )}
        </div>

        {/* Favorite button */}
        {!isCollapsed && !isEditing && item.canFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              item.onToggleFavorite?.();
            }}
            className="ml-2 p-1 rounded hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
            title={item.isFavorited ? "Remove from favorites" : "Add to favorites"}
          >
            {item.isFavorited ? (
              <StarFilled size={16} className="text-yellow-600 dark:text-yellow-400" />
            ) : (
              <Star size={16} className="text-gray-700 dark:text-neutral-300" />
            )}
          </button>
        )}

        {/* Settings, Edit and Delete buttons */}
        {!isCollapsed && !isEditing && item.canSettings && (
          <button
            onClick={handleSettings}
            className="ml-1 p-1 rounded hover:bg-gray-300 dark:hover:bg-neutral-700 transition-colors"
            title="Workspace settings"
          >
            <Settings size={16} className="text-gray-700 dark:text-neutral-300" />
          </button>
        )}

        {!isCollapsed && !isEditing && item.canEdit && (
          <button
            onClick={handleEdit}
            className="ml-1 p-1 rounded hover:bg-gray-300 dark:hover:bg-neutral-700 transition-colors"
            title={isBookmark ? "Edit bookmark" : "Edit workspace"}
          >
            <Edit size={16} className="text-gray-700 dark:text-neutral-300" />
          </button>
        )}

        {!isCollapsed && !isEditing && item.canDelete && (
          <button
            onClick={handleDelete}
            className="ml-1 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            title={isBookmark ? "Delete bookmark" : "Delete workspace"}
          >
            <TrashCan size={16} className="text-red-600 dark:text-red-400" />
          </button>
        )}

        {/* Save and Cancel buttons when editing */}
        {!isCollapsed && isEditing && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSaveEdit?.();
              }}
              className="ml-2 p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
              title="Save"
            >
              <Checkmark size={16} className="text-green-600 dark:text-green-400" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCancelEdit?.();
              }}
              className="ml-1 p-1 rounded hover:bg-gray-300 dark:hover:bg-neutral-700 transition-colors"
              title="Cancel"
            >
              <Close size={16} className="text-gray-700 dark:text-neutral-300" />
            </button>
          </>
        )}

        {item.hasDropdown && (
          <div
            className={`flex items-center justify-center shrink-0 transition-opacity duration-500 ${
              isCollapsed ? 'opacity-0 w-0' : 'opacity-100 ml-2'
            }`}
            style={{ transitionTimingFunction: softSpringEasing }}
          >
            <ChevronDownIcon
              size={16}
              className="text-gray-900 dark:text-neutral-50 transition-transform duration-500"
              style={{
                transitionTimingFunction: softSpringEasing,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function MenuSection({ section, expandedItems, onToggleExpanded, isCollapsed, onEditWorkspace, onDeleteWorkspace, onSettingsWorkspace, onEditBookmark, onDeleteBookmark, editingBookmark, bookmarkName, onBookmarkNameChange, onSaveBookmark, onCancelBookmark }) {
  return (
    <div className="flex flex-col w-full">
      <div
        className={`relative shrink-0 w-full transition-all duration-500 overflow-hidden ${
          isCollapsed ? 'h-0 opacity-0' : 'h-10 opacity-100'
        }`}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div className="flex items-center h-10 px-4">
          <div className="text-sm text-gray-600 dark:text-neutral-400">{section.title}</div>
        </div>
      </div>
      {section.items.map((item, index) => {
        const itemKey = `${section.title}-${index}`;
        const isExpanded = expandedItems.has(itemKey);
        const isEditingThisBookmark = item.bookmarkId && editingBookmark === item.bookmarkId;

        return (
          <div key={itemKey} className="w-full flex flex-col">
            <MenuItem
              item={item}
              isExpanded={isExpanded}
              onToggle={() => onToggleExpanded(itemKey)}
              onItemClick={item.onClick}
              isCollapsed={isCollapsed}
              onSettings={item.workspaceId && item.canSettings ? () => onSettingsWorkspace(item.workspaceId) : undefined}
              onEdit={item.workspaceId ? () => onEditWorkspace(item.workspaceId) : item.bookmarkId ? () => onEditBookmark(item.bookmarkId) : undefined}
              onDelete={item.workspaceId ? () => onDeleteWorkspace(item.workspaceId) : item.bookmarkId ? () => onDeleteBookmark(item.bookmarkId) : undefined}
              isBookmark={item.isBookmark || !!item.bookmarkId}
              isEditing={isEditingThisBookmark}
              editValue={bookmarkName}
              onEditChange={onBookmarkNameChange}
              onSaveEdit={() => onSaveBookmark(item.bookmarkId)}
              onCancelEdit={onCancelBookmark}
            />
          </div>
        );
      })}
    </div>
  );
}

function DetailSidebar({
  activeSection,
  currentWorkspace,
  workspaces,
  bookmarks,
  onAddWorkspace,
  onEditWorkspace,
  onDeleteWorkspace,
  onSettingsWorkspace,
  onWorkspaceClick,
  onBookmarkClick,
  onBookmarkUpdate,
  onBookmarkDelete,
  onCollapsedChange,
  onRefreshWorkspaces
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [bookmarkName, setBookmarkName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookmarkToDelete, setBookmarkToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [favoriteWorkspaces, setFavoriteWorkspaces] = useState([]);
  const [recentWorkspaces, setRecentWorkspaces] = useState([]);

  const toggleExpanded = (itemKey) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });
  };

  const toggleCollapse = () => setIsCollapsed((s) => !s);

  // Fetch favorites and recent workspaces
  React.useEffect(() => {
    const fetchFavoritesAndRecent = async () => {
      try {
        const token = localStorage.getItem('token');

        // Fetch favorites
        const favoritesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/favorites/workspaces`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFavoriteWorkspaces(favoritesRes.data);

        // Fetch recent workspaces
        const recentRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/recent/workspaces`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRecentWorkspaces(recentRes.data);
      } catch (err) {
        console.error('Error fetching favorites and recent workspaces:', err);
      }
    };

    if (activeSection === 'workspaces') {
      fetchFavoritesAndRecent();
    }
  }, [activeSection, workspaces]);

  // Toggle favorite workspace
  const handleToggleFavorite = async (workspaceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/users/favorites/workspace/${workspaceId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      if (response.data.isFavorited) {
        const workspace = workspaces.find(w => w._id === workspaceId);
        if (workspace) {
          setFavoriteWorkspaces([...favoriteWorkspaces, workspace]);
        }
      } else {
        setFavoriteWorkspaces(favoriteWorkspaces.filter(w => w._id !== workspaceId));
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // Notify parent when collapsed state or activeSection changes
  React.useEffect(() => {
    if (onCollapsedChange) {
      if (activeSection === 'workspaces') {
        // When showing workspaces, use actual collapsed state
        onCollapsedChange(isCollapsed);
      } else {
        // When showing other sections, notify that sidebar is effectively "collapsed" (hidden)
        onCollapsedChange(true);
      }
    }
  }, [activeSection, isCollapsed, onCollapsedChange]);

  const handleEditBookmark = (bookmarkId) => {
    const bookmark = bookmarks?.find(b => b._id === bookmarkId);
    if (bookmark) {
      setEditingBookmark(bookmarkId);
      setBookmarkName(bookmark.customName || '');
    }
  };

  const handleSaveBookmarkName = (bookmarkId) => {
    if (bookmarkName.trim()) {
      onBookmarkUpdate(bookmarkId, bookmarkName.trim());
    }
    setEditingBookmark(null);
    setBookmarkName('');
  };

  const handleCancelBookmarkEdit = () => {
    setEditingBookmark(null);
    setBookmarkName('');
  };

  const handleDeleteBookmarkClick = (bookmarkId) => {
    const bookmark = bookmarks?.find(b => b._id === bookmarkId);
    setBookmarkToDelete(bookmark);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteBookmark = () => {
    if (bookmarkToDelete) {
      onBookmarkDelete(bookmarkToDelete._id);
      setBookmarkToDelete(null);
    }
  };

  const getElementTypeLabel = (type) => {
    const labels = {
      title: 'Title',
      description: 'Description',
      macro: 'Macro',
      example: 'Example',
      text: 'Text',
      card: 'Card',
      'sticky-note': 'Note'
    };
    return labels[type] || type;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getElementPreview = (element) => {
    if (!element) return 'No content';

    if (element.type === 'title') {
      const text = stripHtml(element.content?.value || '');
      return text.substring(0, 50) || 'No content';
    }
    if (element.type === 'description') {
      const text = stripHtml(element.content?.value || '');
      const words = text.split(' ').slice(0, 6).join(' ');
      return words || 'No content';
    }
    if (element.type === 'macro') {
      const title = stripHtml(element.content?.title || '');
      return title.substring(0, 50) || stripHtml(element.content?.description || '').substring(0, 50) || 'No content';
    }
    if (element.type === 'example') {
      const currentExample = element.content?.examples?.[element.content?.currentExampleIndex || 0];
      const title = stripHtml(currentExample?.title || '');
      return title.substring(0, 50) || 'No content';
    }
    return element.content?.text || element.content?.title || 'No content';
  };

  const getSectionContent = () => {
    if (activeSection === 'workspaces') {
      // Filter workspaces based on search query
      const filteredWorkspaces = workspaces?.filter(w =>
        w.name.toLowerCase().includes(searchQuery.toLowerCase())
      ) || [];

      // Separate announcements, active-issues, and other workspaces
      const announcements = filteredWorkspaces.find(w => w.type === 'announcements');
      const activeIssues = filteredWorkspaces.find(w => w.type === 'active-issues');
      const otherWorkspaces = filteredWorkspaces.filter(w => w.type !== 'announcements' && w.type !== 'active-issues');

      // Helper function to create workspace item
      const createWorkspaceItem = (workspace, showFavoriteButton = true) => {
        const isOwner = workspace.owner && user && (
          (workspace.owner._id || workspace.owner) === user._id
        );
        const isFavorited = favoriteWorkspaces.some(fw => fw._id === workspace._id);
        const isSpecialWorkspace = workspace.type === 'announcements' || workspace.type === 'active-issues';

        // Special handling for active-issues - navigate to /active-issues instead of workspace
        const handleClick = workspace.type === 'active-issues'
          ? () => navigate('/active-issues')
          : () => onWorkspaceClick(workspace._id);

        return {
          icon: <Home size={16} className="text-gray-900 dark:text-neutral-50" />,
          label: workspace.name,
          isActive: workspace.type === 'active-issues'
            ? window.location.pathname === '/active-issues'
            : currentWorkspace?._id === workspace._id,
          onClick: handleClick,
          workspaceId: workspace._id,
          canSettings: isOwner && workspace.type === 'personal',
          canEdit: workspace.permissions?.canEdit,
          canDelete: !isSpecialWorkspace && workspace.permissions?.canDelete,
          canFavorite: showFavoriteButton && !isSpecialWorkspace,
          isFavorited: isFavorited,
          onToggleFavorite: showFavoriteButton && !isSpecialWorkspace
            ? () => handleToggleFavorite(workspace._id)
            : undefined
        };
      };

      // Recent workspaces items (filtered by search)
      const recentItems = recentWorkspaces
        .filter(w => w.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 5) // Show only last 5
        .map(w => createWorkspaceItem(w, true));

      // Favorite workspaces items (filtered by search)
      const favoriteItems = favoriteWorkspaces
        .filter(w => w.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .map(w => createWorkspaceItem(w, true));

      const workspaceItems = [];

      // Add Announcements first
      if (announcements) {
        workspaceItems.push(createWorkspaceItem(announcements, false));
      }

      // Add Active Issues second (after Announcements)
      if (activeIssues) {
        workspaceItems.push(createWorkspaceItem(activeIssues, false));
      }

      // Add other workspaces
      otherWorkspaces.forEach(workspace => {
        workspaceItems.push(createWorkspaceItem(workspace, true));
      });

      // Add "Add New Workspace" button
      workspaceItems.push({
        icon: <AddLarge size={16} className="text-gray-900 dark:text-neutral-50" />,
        label: 'Add New Workspace',
        onClick: onAddWorkspace,
      });

      // Prepare bookmarks items
      const bookmarkItems = (bookmarks || []).map((bookmark) => {
        const preview = getElementPreview(bookmark.element);
        const typeLabel = getElementTypeLabel(bookmark.element?.type);
        const workspaceName = bookmark.workspace?.name || 'Unknown';

        return {
          icon: <BookmarkIcon size={16} className="text-gray-900 dark:text-neutral-50" />,
          label: bookmark.customName || 'Untitled Bookmark',
          preview: preview,
          typeLabel: typeLabel,
          workspaceIcon: <Home size={12} className="text-gray-600 dark:text-neutral-400" />,
          workspaceName: workspaceName,
          onClick: () => onBookmarkClick(bookmark),
          bookmarkId: bookmark._id,
          canEdit: true,
          canDelete: true,
          isBookmark: true,
        };
      });

      const sections = [];

      // Add Recent section if there are recent workspaces
      if (recentItems.length > 0 && !searchQuery) {
        sections.push({
          title: 'Recent',
          items: recentItems,
        });
      }

      // Add Favorites section if there are favorite workspaces
      if (favoriteItems.length > 0) {
        sections.push({
          title: 'Favorites',
          items: favoriteItems,
        });
      }

      // Add All Workspaces section
      sections.push({
        title: searchQuery ? 'Search Results' : 'All Workspaces',
        items: workspaceItems,
      });

      // Add bookmarks section if there are any bookmarks
      if (bookmarkItems.length > 0 && !searchQuery) {
        sections.push({
          title: 'All Bookmarks',
          items: bookmarkItems,
        });
      }

      return {
        title: 'Workspaces',
        sections,
        showSearch: true,
      };
    }

    return {
      title: activeSection === 'vip-calculator'
        ? 'VIP Progress Calculator'
        : activeSection === 'hash-explorer'
        ? 'Hash Explorer Finder'
        : activeSection === 'developer-dashboard'
        ? 'Developer Dashboard'
        : 'Quick Links',
      sections: [],
    };
  };

  const content = getSectionContent();

  return (
    <aside
      className={`bg-white dark:bg-black flex flex-col gap-4 items-start p-4 transition-all duration-500 h-screen border-r border-gray-200 dark:border-neutral-800 ${
        isCollapsed ? 'w-16 min-w-16 !px-0 justify-center' : 'w-80'
      }`}
      style={{ transitionTimingFunction: softSpringEasing }}
    >
      <SectionTitle title={content.title} onToggleCollapse={toggleCollapse} isCollapsed={isCollapsed} />

      {/* Workspace Search - Only show in workspaces section and when not collapsed */}
      {activeSection === 'workspaces' && !isCollapsed && content.showSearch && (
        <div className="w-full px-2">
          <div className="relative">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search workspaces..."
              className="w-full pl-10 pr-8 py-2 text-sm bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg text-gray-900 dark:text-neutral-50 placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
              >
                <CloseFilled size={14} className="text-gray-400 dark:text-neutral-500" />
              </button>
            )}
          </div>
        </div>
      )}

      <div
        className={`flex flex-col w-full overflow-y-auto transition-all duration-500 ${
          isCollapsed ? 'gap-2 items-center' : 'gap-4 items-start'
        }`}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        {/* Pending Invites - Only show in workspaces section and when not collapsed */}
        {activeSection === 'workspaces' && !isCollapsed && (
          <PendingInvites onInviteResponse={onRefreshWorkspaces} />
        )}

        {content.sections.map((section, index) => (
          <MenuSection
            key={`${activeSection}-${index}`}
            section={section}
            expandedItems={expandedItems}
            onToggleExpanded={toggleExpanded}
            isCollapsed={isCollapsed}
            onEditWorkspace={onEditWorkspace}
            onDeleteWorkspace={onDeleteWorkspace}
            onSettingsWorkspace={onSettingsWorkspace}
            onEditBookmark={handleEditBookmark}
            onDeleteBookmark={handleDeleteBookmarkClick}
            editingBookmark={editingBookmark}
            bookmarkName={bookmarkName}
            onBookmarkNameChange={setBookmarkName}
            onSaveBookmark={handleSaveBookmarkName}
            onCancelBookmark={handleCancelBookmarkEdit}
          />
        ))}
      </div>

      {/* Currently Viewing - Real-time Collaboration Indicator */}
      {activeSection === 'workspaces' && (
        <div className={`mt-auto w-full border-t border-gray-200 dark:border-neutral-800 ${isCollapsed ? 'pt-2' : 'pt-0'}`}>
          <CurrentlyViewing isCollapsed={isCollapsed} />
        </div>
      )}

      {/* Delete Bookmark Dialog */}
      <DeleteBookmarkDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setBookmarkToDelete(null);
        }}
        onConfirm={confirmDeleteBookmark}
        bookmarkName={bookmarkToDelete?.customName || 'this bookmark'}
      />
    </aside>
  );
}

/* ------------------------------ Tools Sidebar ----------------------------- */
// The second rail, beside the main one on every /tools page. Same body
// language as the main rail — a 64px icon rail that opens into a 224px
// labelled panel, remembered per browser, portaled tooltips only while
// collapsed — but with the Claude icon set, and the tools folded into the
// sections toolsList.js declares (pinned Ask Clara AI, then Account, Payments,
// Bets, Bonuses, Utilities). Which tools appear is toolsList.isToolVisible,
// the same rule the dashboard and the quick search use.

const TOOLS_W = 64;
const TOOLS_W_EXPANDED = 224;
const TOOLS_EXPANDED_KEY = 'clara.tools.expanded';

const readStoredToolsExpanded = () => {
  try {
    return localStorage.getItem(TOOLS_EXPANDED_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * One tool row. Collapsed: a 40px square with a tooltip. Expanded: the same
 * 40px icon box on the left (icons stay put when the panel opens) with the
 * name beside it and a clay bar marking the current tool.
 */
function ToolButton({ tool, isActive, expanded, onClick }) {
  const reduce = useReducedMotion();
  const { ref, show, hide, node } = useRailTip(tool.name, !expanded);

  return (
    <>
      <motion.button
        ref={ref}
        type="button"
        aria-label={tool.name}
        aria-current={isActive ? 'page' : undefined}
        onClick={onClick}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        initial={false}
        animate={isActive ? 'active' : 'rest'}
        whileHover={reduce ? undefined : 'hover'}
        whileTap={reduce ? undefined : 'tap'}
        className={`group relative flex items-center outline-none transition-colors duration-200 ${
          expanded ? 'w-full h-9 rounded-lg' : 'size-10 justify-center rounded-xl'
        } ${
          isActive
            ? 'bg-[#f5f5f5] dark:bg-white/[0.06] text-[#111111] dark:text-white'
            : 'text-[#707072] dark:text-white/55 hover:text-[#111111] dark:hover:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/[0.04]'
        }`}
      >
        {isActive && expanded && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full"
            style={{ backgroundColor: '#d97757' }}
          />
        )}
        <span className={`flex items-center justify-center shrink-0 ${expanded ? 'w-10 h-9' : 'size-10'}`}>
          <ClaudeToolIcon
            name={tool.id}
            active={isActive}
            size={16}
            className={`shrink-0 transition-colors ${
              isActive ? '' : 'text-[#9e9ea0] dark:text-white/40 group-hover:text-[#111111] dark:group-hover:text-white'
            }`}
          />
        </span>
        <span
          aria-hidden={!expanded}
          className={`text-left text-[13px] tracking-tight whitespace-nowrap overflow-hidden transition-[opacity,transform] duration-200 ${
            isActive ? 'font-medium' : 'font-normal'
          } ${
            expanded
              ? 'flex-1 min-w-0 truncate opacity-100 translate-x-0 pr-2'
              : 'w-0 flex-none opacity-0 -translate-x-1 pointer-events-none'
          }`}
        >
          {tool.name}
        </span>
      </motion.button>
      {node}
    </>
  );
}

/*
  My tools in the rail (2026-10-02). The agent-built tools a person keeps in the sidebar
  (toolbuilder/sidebarTools.js — per account; until they change it, every tool they built), right
  after Ask Clara AI. Same 40px row as an official tool, with the tool's monogram (its ACP dock tab
  wears the same face) in place of a line icon; opens /tools/my/:id. Expanded, a hover × takes a
  tool out. The section's last row opens SidebarToolsMenu to add or remove tools.
*/
function UserToolButton({ tool, isActive, expanded, onClick, onRemove }) {
  const { ref, show, hide, node } = useRailTip(tool.name, !expanded);
  return (
    <div className="relative group/ut">
      <button
        ref={ref}
        type="button"
        aria-label={tool.name}
        aria-current={isActive ? 'page' : undefined}
        onClick={onClick}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={`group relative flex items-center outline-none transition-colors duration-200 ${
          expanded ? 'w-full h-9 rounded-lg' : 'size-10 justify-center rounded-xl'
        } ${
          isActive
            ? 'bg-[#f5f5f5] dark:bg-white/[0.06] text-[#111111] dark:text-white'
            : 'text-[#707072] dark:text-white/55 hover:text-[#111111] dark:hover:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/[0.04]'
        }`}
      >
        {isActive && expanded && (
          <span aria-hidden="true" className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full" style={{ backgroundColor: '#d97757' }} />
        )}
        <span className={`flex items-center justify-center shrink-0 ${expanded ? 'w-10 h-9' : 'size-10'}`}>
          <ToolMonogram
            name={tool.name}
            size={14}
            className={`shrink-0 transition-colors ${isActive ? '' : 'text-[#9e9ea0] dark:text-white/40 group-hover:text-[#111111] dark:group-hover:text-white'}`}
          />
        </span>
        <span
          aria-hidden={!expanded}
          className={`text-left text-[13px] tracking-tight whitespace-nowrap overflow-hidden transition-[opacity,transform] duration-200 ${
            isActive ? 'font-medium' : 'font-normal'
          } ${
            expanded
              ? 'flex-1 min-w-0 truncate opacity-100 translate-x-0 pr-7'
              : 'w-0 flex-none opacity-0 -translate-x-1 pointer-events-none'
          }`}
        >
          {tool.name}
        </span>
      </button>
      {/* Mouse only: not rendered until the row is hovered, so a tap on a touch screen can never land on it.
          Keyboard and touch take tools out through the section's menu. */}
      {expanded && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Take ${tool.name} out of the sidebar`}
          title="Take out of the sidebar"
          className="absolute right-1 top-1/2 -translate-y-1/2 size-6 hidden group-hover/ut:flex items-center justify-center rounded-md text-[#9e9ea0] dark:text-white/40 hover:text-[#111111] dark:hover:text-white hover:bg-[#e5e5e5] dark:hover:bg-white/[0.1] transition-colors"
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      )}
      {node}
    </div>
  );
}

// The section's last row: an empty monogram slot with a plus — opens the add / remove menu.
function AddUserToolsButton({ anchorRef, expanded, open, label, onClick }) {
  const { ref, show, hide, node } = useRailTip(label, !expanded && !open);
  return (
    <>
      <button
        ref={(el) => { ref.current = el; anchorRef.current = el; }}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onClick}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={`group relative flex items-center outline-none transition-colors duration-200 ${
          expanded ? 'w-full h-9 rounded-lg' : 'size-10 justify-center rounded-xl'
        } ${
          open
            ? 'bg-[#f5f5f5] dark:bg-white/[0.06] text-[#111111] dark:text-white'
            : 'text-[#9e9ea0] dark:text-white/40 hover:text-[#111111] dark:hover:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/[0.04]'
        }`}
      >
        <span className={`flex items-center justify-center shrink-0 ${expanded ? 'w-10 h-9' : 'size-10'}`}>
          <span aria-hidden className="inline-grid place-items-center size-4 border border-dashed border-current">
            <Plus size={10} strokeWidth={3} />
          </span>
        </span>
        <span
          aria-hidden={!expanded}
          className={`text-left text-[13px] tracking-tight whitespace-nowrap overflow-hidden transition-[opacity,transform] duration-200 ${
            expanded ? 'flex-1 min-w-0 truncate opacity-100 translate-x-0 pr-2' : 'w-0 flex-none opacity-0 -translate-x-1 pointer-events-none'
          }`}
        >
          {label}
        </span>
      </button>
      {node}
    </>
  );
}

function ToolsSidebar({ currentTool, onToolClick, onNavigate, onCollapsedChange, onTitleClick }) {
  const { user } = useAuth();
  // Remembered per browser, collapsed by default — and, unlike before, it no
  // longer snaps shut every time you open a tool.
  const [expanded, setExpanded] = useState(readStoredToolsExpanded);
  const toggle = React.useCallback(() => setExpanded((v) => !v), []);

  React.useEffect(() => {
    try { localStorage.setItem(TOOLS_EXPANDED_KEY, expanded ? '1' : '0'); } catch { /* private mode */ }
  }, [expanded]);
  React.useEffect(() => {
    onCollapsedChange?.(!expanded);
  }, [expanded, onCollapsedChange]);

  // Every tool visit lands in the shared recents list, whichever way the tool
  // was opened — ToolsQuickSearch ranks those first on an empty query.
  React.useEffect(() => {
    if (currentTool) pushToolsMru(currentTool);
  }, [currentTool]);

  // My tools: right after the pinned Ask Clara AI, and only for someone with a tool to show or add.
  const location = useLocation();
  const rail = useSidebarTools(user?._id);
  const activeUserTool = /^\/tools\/my\/([^/]+)/.exec(location.pathname)?.[1] || null;
  React.useEffect(() => {
    if (activeUserTool) pushToolsMru(userToolKey(activeUserTool)); // a saved tool's visit is a recent too
  }, [activeUserTool]);
  const [menuOpen, setMenuOpen] = useState(false);
  const addRef = React.useRef(null);
  React.useEffect(() => { setMenuOpen(false); }, [expanded]); // the menu's anchor moves with the rail
  const official = groupedToolsFor(user);
  const mineAt = official[0]?.id === 'pinned' ? 1 : 0;
  const sections = rail.tools.length || rail.available.length
    ? [...official.slice(0, mineAt), { id: 'my-tools', label: 'My tools', mine: true, tools: [] }, ...official.slice(mineAt)]
    : official;
  const addLabel = rail.available.some((t) => !rail.has(t.id)) ? 'Add tools' : 'Choose tools';
  const { ref: openRef, show: showOpenTip, hide: hideOpenTip, node: openTipNode } = useRailTip('Expand tools', !expanded);

  return (
    <aside
      aria-label="Tools"
      className="bg-white dark:bg-[#0A0A0B] flex flex-col items-stretch gap-1 px-3 py-4 h-screen border-r border-[#cacacb] dark:border-white/[0.12] shrink-0 transition-[width] duration-300 motion-reduce:transition-none"
      style={{ width: expanded ? TOOLS_W_EXPANDED : TOOLS_W, transitionTimingFunction: softSpringEasing }}
      data-expanded={expanded ? 'true' : 'false'}
    >
      {/* Header. Collapsed: one "open the panel" button. Expanded: the section
          title (a link back to the Tools dashboard) and the matching close. */}
      {expanded ? (
        <div className="flex items-center h-10">
          <button
            type="button"
            onClick={onTitleClick}
            className="flex-1 min-w-0 h-8 px-3 rounded-lg text-left text-[11px] font-bold uppercase tracking-[0.16em] text-[#111111] dark:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors"
          >
            Tools
          </button>
          <button
            type="button"
            onClick={toggle}
            aria-label="Collapse tools"
            aria-expanded="true"
            className="flex items-center justify-center size-8 rounded-lg text-[#9e9ea0] dark:text-white/45 hover:text-[#111111] dark:hover:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
      ) : (
        <>
          <button
            ref={openRef}
            type="button"
            onClick={toggle}
            onMouseEnter={showOpenTip}
            onMouseLeave={hideOpenTip}
            onFocus={showOpenTip}
            onBlur={hideOpenTip}
            aria-label="Expand tools"
            aria-expanded="false"
            className="flex items-center justify-center size-10 rounded-xl text-[#9e9ea0] dark:text-white/45 hover:text-[#111111] dark:hover:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors"
          >
            <PanelLeftOpen size={16} />
          </button>
          {openTipNode}
        </>
      )}

      <nav
        aria-label="Tools"
        className="flex flex-col w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {sections.map((section, i) => (
          <div key={section.id} className="flex flex-col">
            {expanded
              ? section.label && (
                <div className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#9e9ea0] dark:text-white/35 ${i === 0 ? 'pt-1' : 'pt-3'}`}>
                  {section.label}
                </div>
              )
              : i > 0 && (
                <div aria-hidden="true" className="my-1.5 mx-auto h-px w-5 bg-[#e5e5e5] dark:bg-white/10" />
              )}
            <div className={`flex flex-col ${expanded ? 'gap-0.5' : 'gap-1 items-center'}`}>
              {section.tools.map((tool) => (
                <ToolButton
                  key={tool.id}
                  tool={tool}
                  expanded={expanded}
                  isActive={currentTool === tool.id}
                  onClick={() => onToolClick(tool.id)}
                />
              ))}
              {section.mine && (
                <>
                  {rail.tools.map((tool) => (
                    <UserToolButton
                      key={tool.id}
                      tool={tool}
                      expanded={expanded}
                      isActive={activeUserTool === tool.id}
                      onClick={() => {
                        trackFeatureEvent('tool.open', { target: tool.id, label: tool.name, meta: { via: 'sidebar', mine: tool.mine } });
                        onNavigate(`/tools/my/${tool.id}`);
                      }}
                      onRemove={() => rail.remove(tool.id).catch(() => { /* rolled back in the store */ })}
                    />
                  ))}
                  <AddUserToolsButton anchorRef={addRef} expanded={expanded} open={menuOpen} label={addLabel} onClick={() => setMenuOpen((o) => !o)} />
                </>
              )}
            </div>
          </div>
        ))}
      </nav>
      {menuOpen && (
        <SidebarToolsMenu
          anchor={addRef.current}
          userId={user?._id}
          onClose={() => setMenuOpen(false)}
          onNavigate={(path) => { setMenuOpen(false); onNavigate(path); }}
        />
      )}
    </aside>
  );
}

export default function AppSidebar({
  currentWorkspace,
  workspaces,
  bookmarks,
  onAddWorkspace,
  onEditWorkspace,
  onDeleteWorkspace,
  onSettingsWorkspace,
  onWorkspaceClick,
  onBookmarkClick,
  onBookmarkUpdate,
  onBookmarkDelete,
  activeSection,
  onSectionChange,
  onCollapsedChange,
  onRefreshWorkspaces,
  viewMode,
  currentTool,
  onToolClick
}) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const navigate = useNavigate(); // My tools in the Tools rail navigate themselves (onNavigate)

  // Check if we're in tools section (activeSection is 'tools' or starts with tools route)
  const isToolsSection = activeSection === 'tools' || activeSection?.startsWith?.('tools-');

  // Close mobile drawer when crossing into desktop breakpoint (avoids stuck-open drawer).
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => { if (e.matches) setIsMobileOpen(false); };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Esc closes the mobile drawer. Uses bubble phase, so ProfileModal's capture-phase
  // Esc handler runs first when both are open and won't trigger this.
  React.useEffect(() => {
    if (!isMobileOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileOpen]);

  // Lock body scroll while drawer is open on mobile.
  React.useEffect(() => {
    if (!isMobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMobileOpen]);

  const closeMobile = React.useCallback(() => setIsMobileOpen(false), []);

  const showDetail = activeSection === 'workspaces' && viewMode !== 'post-view';
  const showTools = isToolsSection && (viewMode === 'tools-view' || viewMode !== 'post-view');

  return (
    <>
      {/* Floating mobile hamburger — only visible when drawer is closed, so it doesn't overlap the open drawer. */}
      {!isMobileOpen && (
        <button
          type="button"
          onClick={() => setIsMobileOpen(true)}
          className="lg:hidden fixed top-3 left-3 z-30 p-2 rounded-lg bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm border border-gray-200 dark:border-neutral-800 shadow-sm hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          aria-label="Open menu"
        >
          <Menu size={18} className="text-gray-900 dark:text-neutral-50" />
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Sidebar shell — fixed slide-in drawer on mobile, in-flow on desktop */}
      <div
        className={`
          flex flex-row h-screen bg-white dark:bg-black
          lg:static lg:translate-x-0 lg:z-auto lg:max-w-none lg:shadow-none
          fixed inset-y-0 left-0 z-50 max-w-[92vw] overflow-x-auto shadow-2xl
          transition-transform duration-300 ease-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <IconNavigation
          activeSection={isToolsSection ? 'tools' : activeSection}
          onSectionChange={onSectionChange}
          onOpenProfile={() => setIsProfileOpen(true)}
          onMobileClose={closeMobile}
        />
        {/* Only show DetailSidebar when activeSection is 'workspaces' and not in post-view mode */}
        {showDetail && (
          <DetailSidebar
            activeSection={activeSection}
            currentWorkspace={currentWorkspace}
            workspaces={workspaces}
            bookmarks={bookmarks}
            onAddWorkspace={onAddWorkspace}
            onEditWorkspace={onEditWorkspace}
            onDeleteWorkspace={onDeleteWorkspace}
            onSettingsWorkspace={onSettingsWorkspace}
            onWorkspaceClick={(id) => { onWorkspaceClick?.(id); closeMobile(); }}
            onBookmarkClick={(b) => { onBookmarkClick?.(b); closeMobile(); }}
            onBookmarkUpdate={onBookmarkUpdate}
            onBookmarkDelete={onBookmarkDelete}
            onCollapsedChange={onCollapsedChange}
            onRefreshWorkspaces={onRefreshWorkspaces}
          />
        )}
        {/* Show ToolsSidebar when in tools section */}
        {showTools && (
          <ToolsSidebar
            currentTool={currentTool}
            onToolClick={(id) => { onToolClick?.(id); closeMobile(); }}
            onNavigate={(path) => { navigate(path); closeMobile(); }}
            onCollapsedChange={onCollapsedChange}
            onTitleClick={() => { onSectionChange?.('tools'); closeMobile(); }}
          />
        )}
      </div>

      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />
    </>
  );
}
